import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Receipt, ChevronDown, ChevronRight, CheckCircle2, Clock } from "lucide-react";
import { formatDateDE, dateRangeDE } from "@/lib/utils";
import { tagesart } from "@/lib/tagesart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v, d = 2) => Number(v || 0).toFixed(d).replace(".", ",");
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August",
  "September", "Oktober", "November", "Dezember"];
const SHORT = ["Jan.", "Feb.", "März", "April", "Mai", "Juni", "Juli", "Aug.",
  "Sept.", "Okt.", "Nov.", "Dez."];

// „Oktober 2026" oder „Sept. – Okt. 2026" — auch über Monats- und Jahresgrenzen
const spanLabel = (dates) => {
  const ds = (dates || []).filter(Boolean).sort();
  if (!ds.length) return "";
  const [y1, m1] = ds[0].split("-").map(Number);
  const [y2, m2] = ds[ds.length - 1].split("-").map(Number);
  if (y1 === y2 && m1 === m2) return `${MONTHS[m1 - 1]} ${y1}`;
  if (y1 === y2) return `${SHORT[m1 - 1]} – ${SHORT[m2 - 1]} ${y1}`;
  return `${SHORT[m1 - 1]} ${y1} – ${SHORT[m2 - 1]} ${y2}`;
};

export default function StundenkontoTab({ pwd, onCreateInvoice }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [showInvoiced, setShowInvoiced] = useState(false);
  const [sel, setSel] = useState({});          // shiftId -> true/false (überschreibt Vorauswahl)

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stundennachweise`, { headers: { "X-Admin-Password": pwd } });
      setShifts(data || []);
    } catch {
      toast.error("Stundenkonto konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  // Vorauswahl: alle noch nicht abgerechneten Einsätze
  const isSel = useCallback((s) => sel[s.id] ?? !s.invoiced_at, [sel]);
  const toggle = (id, val) => setSel((p) => ({ ...p, [id]: val }));

  // Gruppierung nur nach Kunde — monatsübergreifend abrechenbar
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of shifts) {
      if (!s.confirmed_at) continue;                       // nur bestätigte Stunden
      if (!showInvoiced && s.invoiced_at) continue;
      const key = (s.betrieb || "").toLowerCase();
      if (!map.has(key)) map.set(key, { key, betrieb: s.betrieb, items: [] });
      map.get(key).items.push(s);
    }
    return [...map.values()]
      .map((g) => {
        const items = g.items.slice().sort((a, b) => (a.datum || "").localeCompare(b.datum || ""));
        const chosen = items.filter(isSel);
        const sum = (arr, f) => Math.round(arr.reduce((a, s) => a + Number(f(s) || 0), 0) * 100) / 100;
        return {
          ...g,
          items,
          chosen,
          hours: sum(chosen, (s) => s.hours),
          net: sum(chosen, (s) => s.total_net_eur),
          span: spanLabel(chosen.map((s) => s.datum)),
          lastDate: items[items.length - 1]?.datum || "",
        };
      })
      .sort((a, b) => (b.lastDate + b.betrieb).localeCompare(a.lastDate + a.betrieb));
  }, [shifts, showInvoiced, isSel]);

  const openTotals = useMemo(() => groups.reduce((a, g) => ({
    hours: Math.round((a.hours + g.hours) * 100) / 100,
    net: Math.round((a.net + g.net) * 100) / 100,
  }), { hours: 0, net: 0 }), [groups]);

  const buildInvoice = async (g) => {
    const use = g.chosen;
    if (!use.length) { toast.error("Bitte mindestens einen Einsatztag auswählen"); return; }
    const first = use[0];
    const last = use[use.length - 1];
    const zeitraum = dateRangeDE(first.datum, last.datum);
    // Stammdaten aus der Kundenkartei (Rechnungsadresse, Anschrift, Ansprechpartner)
    let kunde = null;
    try {
      const { data } = await axios.get(`${API}/admin/kunden`, { headers: { "X-Admin-Password": pwd } });
      kunde = (data?.kunden || data || []).find(
        (k) => (k.betrieb || "").trim().toLowerCase() === (g.betrieb || "").trim().toLowerCase()) || null;
    } catch { /* ohne Kartei einfach mit den Nachweis-Daten weiter */ }
    const adresse = kunde
      ? [kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")
      : "";
    onCreateInvoice?.({
      form: {
        betrieb: g.betrieb,
        ansprechpartner: kunde?.ansprechpartner || first.kontakt_name || "",
        email: kunde?.rechnung_email || kunde?.email || first.email || "",
        telefon: kunde?.telefon || first.phone || "",
        ort: adresse,
        einsatzdatum: last.datum,
        leistungszeitraum: zeitraum,
        notes: `Sammelrechnung für ${g.span} — ${use.length} Einsätze (${zeitraum}), ${num(g.hours)} Stunden.`,
        admin_notes: `Stundenkonto ${g.span} · Nachweise: ${use.map((s) => s.nachweis_number).sort().join(", ")}`,
      },
      lineItems: use.map((s) => {
        const t = tagesart(s.datum);
        return {
          type_key: t.we ? "mietkoch_we" : "mietkoch_werktag",
          name: `Mietkoch-Stunden ${t.tag}, ${formatDateDE(s.datum)}`,
          description: `${s.von}–${s.bis} Uhr${s.pause_min ? ` (Pause ${s.pause_min} Min.)` : ""} · ${t.art}${t.art !== "Werktag" ? "s-Satz" : ""} · bestätigt durch ${s.confirmed_by}`,
          quantity: Number(s.hours || 0),
          unit_name: "Stunde",
          unit_price_net: Number(s.stundensatz_eur || 0),
          tax_rate: 19,
        };
      }),
      shiftIds: use.map((s) => s.id),
    });
    toast.info(kunde
      ? "Positionen und Kundendaten sind vorbefüllt — Rechnungsadresse kommt aus der Kundenkartei"
      : "Positionen sind vorbefüllt — Leistungsdatum und Texte kannst du frei anpassen");
  };

  return (
    <div data-testid="stundenkonto-tab" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Stundenkonto</p>
          <p className="text-sm text-[#757067] mt-1 max-w-2xl">
            Alle bestätigten Stunden, gesammelt nach Kunde — auch über Monatsgrenzen hinweg.
            Öffne den Kunden, hake die Tage an, die auf die Rechnung sollen, und erzeuge die
            Sammelrechnung. Jeder Einsatztag wird eine eigene Position.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-[#757067] cursor-pointer">
          <input type="checkbox" checked={showInvoiced} onChange={(e) => setShowInvoiced(e.target.checked)}
            data-testid="stundenkonto-show-invoiced" className="accent-[#BA6D52]" />
          Bereits abgerechnete anzeigen
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border border-[#E3DECE] bg-white p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Kunden mit offenen Stunden</p>
          <p className="font-serif text-2xl text-[#2C2A26] mt-1" data-testid="stundenkonto-groups">{groups.length}</p>
        </div>
        <div className="border border-[#E3DECE] bg-white p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Stunden</p>
          <p className="font-serif text-2xl text-[#2C2A26] mt-1">{num(openTotals.hours)}</p>
        </div>
        <div className="border border-[#E3DECE] bg-white p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Summe netto</p>
          <p className="font-serif text-2xl text-[#BA6D52] mt-1" data-testid="stundenkonto-net">{num(openTotals.net)} €</p>
        </div>
      </div>

      {loading && <p className="text-sm text-[#757067]">Lade …</p>}
      {!loading && groups.length === 0 && (
        <p className="text-sm text-[#757067] italic py-8 text-center border border-dashed border-[#E3DECE]">
          Noch keine bestätigten Stunden zum Abrechnen.
        </p>
      )}

      <div className="space-y-3">
        {groups.map((g) => {
          const open = openKey === g.key;
          return (
            <div key={g.key} data-testid={`stundenkonto-group-${g.key}`} className="border border-[#E3DECE] bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 md:p-5">
                <button onClick={() => setOpenKey(open ? null : g.key)}
                  data-testid={`stundenkonto-toggle-${g.key}`}
                  className="flex items-start gap-3 text-left min-w-0">
                  {open ? <ChevronDown size={16} className="text-[#BA6D52] mt-1 shrink-0" />
                        : <ChevronRight size={16} className="text-[#BA6D52] mt-1 shrink-0" />}
                  <span className="min-w-0">
                    <span className="block font-serif text-lg text-[#2C2A26]">{g.betrieb}</span>
                    <span className="block text-xs text-[#757067] mt-0.5">
                      {g.chosen.length} von {g.items.length} Einsätzen gewählt
                      {g.span ? ` · ${g.span}` : ""} · <b>{num(g.hours)} Std.</b> · {num(g.net)} € netto
                    </span>
                  </span>
                </button>
                <button onClick={() => buildInvoice(g)} data-testid={`stundenkonto-invoice-${g.key}`}
                  className="inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-5 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
                  <Receipt size={13} /> Sammelrechnung
                </button>
              </div>

              {open && (
                <div className="border-t border-[#E3DECE] bg-[#FAFAF7] px-4 md:px-5 py-4">
                  <div className="flex flex-wrap gap-3 mb-3">
                    <button onClick={() => setSel((p) => ({ ...p, ...Object.fromEntries(g.items.map((s) => [s.id, true])) }))}
                      data-testid={`stundenkonto-all-${g.key}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-white px-3 py-1.5">
                      Alle Tage
                    </button>
                    <button onClick={() => setSel((p) => ({ ...p, ...Object.fromEntries(g.items.map((s) => [s.id, false])) }))}
                      data-testid={`stundenkonto-none-${g.key}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-white px-3 py-1.5">
                      Keinen
                    </button>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">
                      <tr>
                        <th className="text-left pb-2 w-8">Mit</th>
                        <th className="text-left pb-2">Datum</th>
                        <th className="text-left pb-2">Zeit</th>
                        <th className="text-right pb-2">Std.</th>
                        <th className="text-right pb-2">Netto</th>
                        <th className="text-right pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((s) => (
                        <tr key={s.id} className={`border-t border-[#E3DECE] ${isSel(s) ? "" : "opacity-45"}`}>
                          <td className="py-2">
                            <input type="checkbox" checked={isSel(s)}
                              onChange={(e) => toggle(s.id, e.target.checked)}
                              data-testid={`stundenkonto-pick-${s.id}`}
                              className="accent-[#BA6D52]" />
                          </td>
                          <td className="py-2">{formatDateDE(s.datum)}</td>
                          <td className="py-2 text-[#757067]">{s.von}–{s.bis}</td>
                          <td className="py-2 text-right">{num(s.hours)}</td>
                          <td className="py-2 text-right">{num(s.total_net_eur)} €</td>
                          <td className="py-2 text-right">
                            {s.invoiced_at ? (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#BA6D52]">
                                <CheckCircle2 size={11} /> abgerechnet
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-emerald-700">
                                <Clock size={11} /> offen
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[#BA6D52]">
                        <td className="pt-2 text-xs uppercase tracking-[0.15em] text-[#757067]" colSpan={3}>
                          Gewählt ({g.chosen.length})
                        </td>
                        <td className="pt-2 text-right font-medium">{num(g.hours)}</td>
                        <td className="pt-2 text-right font-serif text-lg text-[#BA6D52]">{num(g.net)} €</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                  <p className="text-xs text-[#757067] italic mt-3">
                    Zzgl. 19 % MwSt. bei der Rechnungsstellung · brutto {num(Math.round(g.net * 119) / 100)} €
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
